// eslint-disable-next-line import/no-cycle
import { CropsEnum, PlotStatusEnum, PlotType } from './plot';

export enum PlotGroupFiltersEnum {
  country = 'country',
  plot__crops = 'plot__crops',
  plot__type = 'plot__type',
  plot__labels__id = 'plot__labels__id',
  start_date = 'start_date',
  end_date = 'end_date',
  deforested = 'deforested',
}

export enum PlotGroupGroupByEnum {
  labels = 'labels',
}

export enum PlotGroupAdditionalPlotsEnum {
  additional_plot_ids = 'additional_plot_ids',
}

export const NotAllowedStatus = {
  // These statuses are not allowed in the plot group filters
  [PlotStatusEnum.failed]: PlotStatusEnum.failed,
  [PlotStatusEnum.new_plot]: PlotStatusEnum.new_plot,
} as const;

export type PlotGroupFilters = {
  [PlotGroupFiltersEnum.country]: string[];
  [PlotGroupFiltersEnum.plot__crops]: CropsEnum[];
  [PlotGroupFiltersEnum.plot__type]: PlotType[];
  [PlotGroupFiltersEnum.plot__labels__id]: PlotLabelValue[];
  [PlotGroupFiltersEnum.start_date]: string | null;
  [PlotGroupFiltersEnum.end_date]: string | null;
  [PlotGroupFiltersEnum.deforested]: boolean | null;
};

export type PlotGroupGroupBy = {
  [PlotGroupGroupByEnum.labels]: PlotLabelName | null;
};

export enum FILTER_OPERATORS {
  in = 'in',
  exact = 'exact',
  contains = 'contains',
  gt = 'gt',
  lt = 'lt',
  gte = 'gte',
  lte = 'lte',
}

export type PlotLabelName = {
  id: string;
  name: string;
  organization: string;
};

export type PlotLabelValue = {
  id: string;
  label_name: string;
  label_name_id: string;
  value: string;
};

export const PLOT_GROUP_FILTER_OPERATOR = {
  [PlotGroupFiltersEnum.country]: FILTER_OPERATORS.in,
  [PlotGroupFiltersEnum.plot__crops]: FILTER_OPERATORS.in,
  [PlotGroupFiltersEnum.plot__type]: FILTER_OPERATORS.in,
  [PlotGroupFiltersEnum.plot__labels__id]: FILTER_OPERATORS.in,
  [PlotGroupFiltersEnum.start_date]: FILTER_OPERATORS.gte,
  [PlotGroupFiltersEnum.end_date]: FILTER_OPERATORS.lte,
  [PlotGroupFiltersEnum.deforested]: {
    true: FILTER_OPERATORS.gt,
    false: FILTER_OPERATORS.exact,
  },
} as const;

export enum SortOptions {
  asc = 'asc',
  desc = 'desc',
  none = 'none',
}

export enum PlotGroupSortEnum {
  // TODO: MVP-CW - Uncomment and implement these fields when needed
  // assetName: 'assetName',
  // country: 'country',
  // hectares: 'hectares',
  // stewardValue: 'stewardValue',
  // valueAtRisk: 'valueAtRisk',
  // natureAtRisk: 'natureAtRisk',
  plot__name = 'plot__name',
  plot__area = 'plot__area',
  ag_carbon = 'ag_carbon',
  bg_carbon = 'bg_carbon',
  water = 'water',
  pofh = 'pofh',
  ag_carbon_trend = 'ag_carbon_trend',
  bg_carbon_trend = 'bg_carbon_trend',
  water_trend = 'water_trend',
  pofh_trend = 'pofh_trend',
  stewarded_value = 'stewarded_value',
}

export type PlotGroupSort = {
  // TODO: MVP-CW - Uncomment and implement these fields when needed
  // [PlotGroupSortEnum.assetName]: SortOptions | undefined;
  // [PlotGroupSortEnum.country]: SortOptions | undefined;
  // [PlotGroupSortEnum.hectares]: SortOptions | undefined;
  // [PlotGroupSortEnum.stewardValue]: SortOptions | undefined;
  // [PlotGroupSortEnum.valueAtRisk]: SortOptions | undefined;
  // [PlotGroupSortEnum.natureAtRisk]: SortOptions | undefined;
  [PlotGroupSortEnum.plot__name]: SortOptions | undefined;
  [PlotGroupSortEnum.plot__area]: SortOptions | undefined;
  [PlotGroupSortEnum.ag_carbon]: SortOptions | undefined;
  [PlotGroupSortEnum.bg_carbon]: SortOptions | undefined;
  [PlotGroupSortEnum.water]: SortOptions | undefined;
  [PlotGroupSortEnum.pofh]: SortOptions | undefined;
  [PlotGroupSortEnum.ag_carbon_trend]: SortOptions | undefined;
  [PlotGroupSortEnum.bg_carbon_trend]: SortOptions | undefined;
  [PlotGroupSortEnum.water_trend]: SortOptions | undefined;
  [PlotGroupSortEnum.pofh_trend]: SortOptions | undefined;
  [PlotGroupSortEnum.stewarded_value]: SortOptions | undefined;
};

export type PlotGroup = {
  id: string | null;
  groupBy: PlotGroupGroupBy;
  filters: PlotGroupFilters;
  additional_plot_ids?: string[];
  sort: PlotGroupSort;
  name: string | null;
  active: boolean;
  total_plots?: number;
  total_area?: number;
};

export const CountryCodeNameMap = {
  // Africa
  DZA: 'Algeria',
  AGO: 'Angola',
  BEN: 'Benin',
  BWA: 'Botswana',
  BFA: 'Burkina Faso',
  BDI: 'Burundi',
  CPV: 'Cabo Verde',
  CMR: 'Cameroon',
  CAF: 'Central African Republic',
  TCD: 'Chad',
  COM: 'Comoros',
  COD: 'Congo, Democratic Republic of the',
  COG: 'Congo, Republic of the',
  CIV: "Côte d'Ivoire",
  DJI: 'Djibouti',
  EGY: 'Egypt',
  GNQ: 'Equatorial Guinea',
  ERI: 'Eritrea',
  SWZ: 'Eswatini',
  ETH: 'Ethiopia',
  GAB: 'Gabon',
  GMB: 'Gambia',
  GHA: 'Ghana',
  GIN: 'Guinea',
  GNB: 'Guinea-Bissau',
  KEN: 'Kenya',
  LSO: 'Lesotho',
  LBR: 'Liberia',
  LBY: 'Libya',
  MDG: 'Madagascar',
  MWI: 'Malawi',
  MLI: 'Mali',
  MRT: 'Mauritania',
  MUS: 'Mauritius',
  MAR: 'Morocco',
  MOZ: 'Mozambique',
  NAM: 'Namibia',
  NER: 'Niger',
  NGA: 'Nigeria',
  RWA: 'Rwanda',
  STP: 'Sao Tome and Principe',
  SEN: 'Senegal',
  SYC: 'Seychelles',
  SLE: 'Sierra Leone',
  SOM: 'Somalia',
  ZAF: 'South Africa',
  SSD: 'South Sudan',
  SDN: 'Sudan',
  TZA: 'Tanzania',
  TGO: 'Togo',
  TUN: 'Tunisia',
  UGA: 'Uganda',
  ZMB: 'Zambia',
  ZWE: 'Zimbabwe',

  // Americas
  ATG: 'Antigua and Barbuda',
  ARG: 'Argentina',
  BHS: 'Bahamas',
  BRB: 'Barbados',
  BLZ: 'Belize',
  BOL: 'Bolivia',
  BRA: 'Brazil',
  CAN: 'Canada',
  CHL: 'Chile',
  COL: 'Colombia',
  CRI: 'Costa Rica',
  CUB: 'Cuba',
  DMA: 'Dominica',
  DOM: 'Dominican Republic',
  ECU: 'Ecuador',
  SLV: 'El Salvador',
  GRD: 'Grenada',
  GTM: 'Guatemala',
  GUY: 'Guyana',
  HTI: 'Haiti',
  HND: 'Honduras',
  JAM: 'Jamaica',
  MEX: 'Mexico',
  NIC: 'Nicaragua',
  PAN: 'Panama',
  PRY: 'Paraguay',
  PER: 'Peru',
  KNA: 'Saint Kitts and Nevis',
  LCA: 'Saint Lucia',
  VCT: 'Saint Vincent and the Grenadines',
  SUR: 'Suriname',
  TTO: 'Trinidad and Tobago',
  USA: 'United States',
  URY: 'Uruguay',
  VEN: 'Venezuela',

  // Asia
  AFG: 'Afghanistan',
  ARM: 'Armenia',
  AZE: 'Azerbaijan',
  BHR: 'Bahrain',
  BGD: 'Bangladesh',
  BTN: 'Bhutan',
  BRN: 'Brunei',
  KHM: 'Cambodia',
  CHN: 'China',
  CYP: 'Cyprus',
  GEO: 'Georgia',
  IND: 'India',
  IDN: 'Indonesia',
  IRN: 'Iran',
  IRQ: 'Iraq',
  ISR: 'Israel',
  JPN: 'Japan',
  JOR: 'Jordan',
  KAZ: 'Kazakhstan',
  KWT: 'Kuwait',
  KGZ: 'Kyrgyzstan',
  LAO: 'Laos',
  LBN: 'Lebanon',
  MYS: 'Malaysia',
  MDV: 'Maldives',
  MNG: 'Mongolia',
  MMR: 'Myanmar',
  NPL: 'Nepal',
  PRK: 'North Korea',
  OMN: 'Oman',
  PAK: 'Pakistan',
  PSE: 'Palestine',
  PHL: 'Philippines',
  QAT: 'Qatar',
  SAU: 'Saudi Arabia',
  SGP: 'Singapore',
  KOR: 'South Korea',
  LKA: 'Sri Lanka',
  SYR: 'Syria',
  TWN: 'Taiwan',
  TJK: 'Tajikistan',
  THA: 'Thailand',
  TLS: 'Timor-Leste',
  TKM: 'Turkmenistan',
  ARE: 'United Arab Emirates',
  UZB: 'Uzbekistan',
  VNM: 'Viet Nam',
  YEM: 'Yemen',

  // Europe
  ALB: 'Albania',
  AND: 'Andorra',
  AUT: 'Austria',
  BLR: 'Belarus',
  BEL: 'Belgium',
  BIH: 'Bosnia and Herzegovina',
  BGR: 'Bulgaria',
  HRV: 'Croatia',
  CZE: 'Czech Republic',
  DNK: 'Denmark',
  EST: 'Estonia',
  FIN: 'Finland',
  FRA: 'France',
  DEU: 'Germany',
  GRC: 'Greece',
  HUN: 'Hungary',
  ISL: 'Iceland',
  IRL: 'Ireland',
  ITA: 'Italy',
  LVA: 'Latvia',
  LIE: 'Liechtenstein',
  LTU: 'Lithuania',
  LUX: 'Luxembourg',
  MLT: 'Malta',
  MDA: 'Moldova',
  MCO: 'Monaco',
  MNE: 'Montenegro',
  NLD: 'Netherlands',
  MKD: 'North Macedonia',
  NOR: 'Norway',
  POL: 'Poland',
  PRT: 'Portugal',
  ROU: 'Romania',
  RUS: 'Russia',
  SMR: 'San Marino',
  SRB: 'Serbia',
  SVK: 'Slovakia',
  SVN: 'Slovenia',
  ESP: 'Spain',
  SWE: 'Sweden',
  CHE: 'Switzerland',
  UKR: 'Ukraine',
  GBR: 'United Kingdom',
  VAT: 'Vatican City',

  // Oceania
  AUS: 'Australia',
  FJI: 'Fiji',
  KIR: 'Kiribati',
  MHL: 'Marshall Islands',
  FSM: 'Micronesia',
  NRU: 'Nauru',
  NZL: 'New Zealand',
  PLW: 'Palau',
  PNG: 'Papua New Guinea',
  WSM: 'Samoa',
  SLB: 'Solomon Islands',
  TON: 'Tonga',
  TUV: 'Tuvalu',
  VUT: 'Vanuatu',
} as const;

export const DEFAULT_PLOT_GROUP_FILTERS: PlotGroupFilters = Object.fromEntries(
  Object.keys(PlotGroupFiltersEnum).map((key) => {
    if ([PlotGroupFiltersEnum.start_date, PlotGroupFiltersEnum.end_date].includes(key as PlotGroupFiltersEnum)) {
      return [key, null];
    }
    return [key, []];
  }),
) as unknown as PlotGroupFilters;

export const DEFAULT_PLOT_GROUP_SORT: PlotGroupSort = Object.fromEntries(
  Object.keys(PlotGroupSortEnum).map((key) => [key, undefined]),
) as { [K in keyof typeof PlotGroupSortEnum]: undefined };

export const DEFAULT_PLOT_GROUP_GROUP_BY = {
  [PlotGroupGroupByEnum.labels]: null,
};

export const mockDefaultPlotGroup = {
  id: 'plot_group_id',
  groupBy: DEFAULT_PLOT_GROUP_GROUP_BY,
  filters: DEFAULT_PLOT_GROUP_FILTERS,
  additional_plot_ids: [],
  sort: DEFAULT_PLOT_GROUP_SORT,
  name: `default_123`,
  active: true,
};
